import { Euler, PerspectiveCamera, Quaternion, Spherical, Vector3 } from 'three'
import * as TWEEN from '@tweenjs/tween.js'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'

import { ModelControls } from './utils/ModelControls'
import IPointOfView from './interfaces/point-of-view.interface'
import ICameraConstraints from './interfaces/camera-constraints.interface'
import { CameraConstraints } from '../../../trillion-common/src/objects/camera-constraints.object'

export type IControls = {
  minDistance: number
  maxDistance: number
  maxAzimuthAngle: number
  minAzimuthAngle: number
  maxPolarAngle: number
  minPolarAngle: number
}

export interface ICameraController {
  setPointOfView: (
    pointOfView: IPointOfView,
    defaultCameraConstraints: CameraConstraints,
    isFreeMoveCameraModeEnabled: boolean,
  ) => void
  getDistance: () => number
  addOnStart: (callback: () => void) => void
  removeOnStart: (callback: () => void) => void
  destroy: () => void
  start: () => void
  stop: () => void
  update: () => void
}

export function CameraController(
  camera: PerspectiveCamera,
  controls: ModelControls | OrbitControls,
): ICameraController {
  const animationSpeed = 1000

  let defaultTarget: Vector3 | undefined = undefined

  let tweenOrbit: TWEEN.Tween<Spherical>
  let tweenQuaternion: TWEEN.Tween<Quaternion>
  let tweenVector: TWEEN.Tween<Vector3>

  const setPointOfView = (
    pointOfView: IPointOfView,
    defaultCameraConstraints: CameraConstraints,
    isFreeMoveCameraModeEnabled: boolean,
  ) => {
    if (tweenOrbit) TWEEN.remove(tweenOrbit)
    if (tweenQuaternion) TWEEN.remove(tweenQuaternion)
    if (tweenVector) TWEEN.remove(tweenVector)

    const curPosition = camera.position.clone()
    const curSphericalPosition = new Spherical().setFromVector3(curPosition)
    const targetSphericalPosition = new Spherical().setFromVector3(pointOfView.cameraPosition)

    const object3D = controls?.object

    setCameraConstraints(
      isFreeMoveCameraModeEnabled ? new CameraConstraints() : defaultCameraConstraints,
    )
    tweenOrbit = new TWEEN.Tween(curSphericalPosition)
      .to(targetSphericalPosition, animationSpeed)
      .easing(TWEEN.Easing.Quadratic.InOut)
      .onUpdate(() => {
        camera.position.copy(new Vector3().setFromSpherical(curSphericalPosition))
      })
      .onStart(() => {
        if (!defaultTarget) defaultTarget = controls.target.clone()
        const curTarget = controls.target.clone()
        const targetTarget = pointOfView.lookAtPosition || defaultTarget

        tweenVector = new TWEEN.Tween(curTarget)
          .to(targetTarget, animationSpeed)
          .easing(TWEEN.Easing.Quadratic.InOut)
          .onUpdate(() => {
            controls.target.copy(curTarget)
            controls.update()
          })
          .start()

        if (object3D) {
          const currentObjectQuaternion = new Quaternion().setFromEuler(object3D.rotation)
          const targetObjectQuaternion = new Quaternion().setFromEuler(new Euler())

          tweenQuaternion = new TWEEN.Tween(currentObjectQuaternion)
            .to(targetObjectQuaternion, animationSpeed)
            .easing(TWEEN.Easing.Quadratic.InOut)
            .onUpdate(() => {
              object3D.rotation.setFromQuaternion(currentObjectQuaternion)
            })
            .start()
        }
      })
      .start()
      .onComplete(() => {
        const constraints = isFreeMoveCameraModeEnabled
          ? new CameraConstraints()
          : pointOfView.cameraConstraints

        setCameraConstraints(constraints ?? defaultCameraConstraints)
      })
  }

  const getDistance = () => {
    return controls.getDistance()
  }

  const onStartCallbacks: (() => void)[] = []
  const addOnStart = (callback: () => void) => {
    onStartCallbacks.push(callback)
  }
  const removeOnStart = (callback: () => void) => {
    const index = onStartCallbacks.indexOf(callback)
    if (index !== -1) {
      onStartCallbacks.splice(index, 1)
    }
  }

  const setCameraConstraints = (constraints: ICameraConstraints) => {
    controls.minDistance = constraints.minDistance
    controls.maxDistance = constraints.maxDistance
    controls.maxAzimuthAngle = constraints.maxAzimuthAngle
    controls.minAzimuthAngle = constraints.minAzimuthAngle
    controls.maxPolarAngle = constraints.maxPolarAngle
    controls.minPolarAngle = constraints.minPolarAngle
  }

  const destroy = () => {
    onStartCallbacks.length = 0
  }

  controls.addEventListener('start', () => {
    onStartCallbacks.forEach((callback) => callback())
  })

  const start = () => {
    controls.enabled = true
  }

  const stop = () => {
    controls.enabled = false
  }

  const update = () => {
    controls.update()
  }

  return {
    setPointOfView,
    getDistance,
    addOnStart,
    removeOnStart,
    destroy,
    start,
    stop,
    update,
  }
}
